import { EquipmentListContext } from 'app/components/ReactContexts/equipmentListContext';
import { useMemo, useContext } from 'react';
import { sharesApi } from 'services/api/shares';
import {
  SharedItem,
  SharedItemShareType,
  SharedItemsResponse,
} from 'services/api/shares/types';
import { ISharedEquipmentEntry, ISharedUser } from 'types/IEquipmentEntry';

const useGetEquipmentData = (
  equipmentId?: string,
): {
  equipments?: ISharedEquipmentEntry[];
  isLoading: boolean;
  sharesData?: SharedItemsResponse;
  equipmentList?: ISharedEquipmentEntry[];
  selectedEquipment?: ISharedEquipmentEntry;
  sharedUsers?: ISharedUser[];
  refetch: () => void;
} => {
  const {
    data: sharesData,
    isLoading,
    refetch,
  } = sharesApi.useGetSharesQuery();

  const {
    equipmentListData: { equipmentList },
  } = useContext(EquipmentListContext);

  const equipments = useMemo(() => {
    if (!equipmentList || isLoading) return [];

    const individualShareMap = new Map<string, SharedItem>();
    const fleetShareMap = new Map<string, SharedItem>();

    const allShares = [
      ...(sharesData?.equipment || []),
      ...(sharesData?.fleets || []),
    ];

    for (const share of allShares) {
      const { shareType, exposedId, exposedEquipmentIds } =
        share.sharedItemDetails;
      if (shareType === SharedItemShareType.EQUIPMENT && exposedId) {
        individualShareMap.set(exposedId, share);
      } else if (
        shareType === SharedItemShareType.FLEET &&
        exposedEquipmentIds
      ) {
        for (const eqId of exposedEquipmentIds) {
          fleetShareMap.set(eqId, share);
        }
      }
    }

    return equipmentList.map(equipment => {
      const equipmentSharesData = individualShareMap.get(equipment.id);
      const equipmentFleetData = fleetShareMap.get(equipment.id);

      return {
        ...equipment,
        isSharedIndividually: !!equipmentSharesData,
        isSharedAsPartOfFleet: !!equipmentFleetData,
        shareData: equipmentSharesData,
        fleetData: equipmentFleetData,
      };
    });
  }, [equipmentList, sharesData]);

  const selectedEquipment = useMemo(() => {
    if (!equipmentId || !equipments || isLoading) return;

    return equipments.find(equipment => equipment.id === equipmentId);
  }, [equipments, equipmentId]);

  return {
    equipments,
    isLoading,
    sharesData,
    selectedEquipment,
    refetch,
  };
};

export default useGetEquipmentData;
